import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FeatureCollection } from 'geojson';
import { finalize, tap } from 'rxjs/operators';
import { VertexStore } from 'src/app/geometrics-form/vertexes/state/vertex.store';
import { ProjectUI } from './project-ui';
import { ProjectValidation } from './project-validation';
import { Project, ProjectUploadStatus } from './project.model';
import { ProjectStore } from './project.store';

@Injectable({ providedIn: 'root' })
export class ProjectService {

  constructor(
    private projectStore: ProjectStore,
    private http: HttpClient,
    private vertexStore: VertexStore
  ) { }

  get() {
    this.projectStore.setLoading(true);
    return this.http.get<Project[]>('/api/project/').pipe(
      tap(entities => {
        this.projectStore.set(entities);
      }),
      finalize(() => {
        this.projectStore.setLoading(false);
      })
    );
  }

  add(project: Project) {
    return this.http.post<Project>('/api/project/', project).pipe(tap((entity) => {
      this.projectStore.add(entity);
    }));
  }

  update(id: number, project: Partial<Project>) {
    return this.http.put<Project>(`/api/project/${id}`, project).pipe(tap((entity) => {
      this.projectStore.update(id, entity);
    }));
  }

  remove(id: number) {
    return this.http.delete(`/api/project/${id}`)
      .pipe(tap(
        () => this.projectStore.remove(id)
      ));
  }

  setActive(id: number) {
    this.projectStore.setActive(id);
  }

  revomeActive() {
    this.projectStore.setActive(null);
  }

  getCompleted(id: number) {
    this.projectStore.setLoading(true);
    return this.http.get<Project>(`/api/project/${id}`).pipe(
      tap(entity => {
        this.projectStore.upsert(id, entity);
      }),
      finalize(() => this.projectStore.setLoading(false))
    );
  }

  upload(id: number, file: any) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post(`/api/project/${id}/upload`, formData, {
      reportProgress: true,
      observe: 'events'
    });
  }

  getUploadStatus(id: number) {
    return this.http.get<ProjectUploadStatus>(`/api/project/${id}/upload`);
  }

  validate(id: number) {
    this.projectStore.setLoading(true);
    this.vertexStore.update(null, { invalid: false });
    return this.http.get<ProjectValidation>(`/api/project/${id}/validate`)
      .pipe(
        tap(v => {
          if (v.disconnected) {
            this.vertexStore.update(v.disconnected, { invalid: true });
            this.projectStore.update(id, { validated: v.validated });
          }
        }),
        finalize(() => this.projectStore.setLoading(false))
      );
  }

  updateUI(id: number, ui: Partial<ProjectUI>): void {
    this.projectStore.ui.update(id, ui);
  }

  isValid(projectUI: ProjectUI) {
    if (projectUI.centralOffices.length !== 1) {
      return false;
    }
    if (projectUI.vertexesWithoutEdges.length > 0) {
      return false;
    }
    return projectUI.centralOfficeConnectedToBox && projectUI.hasVertexes && projectUI.hasEdges && projectUI.hasBuilding;
  }

  getSquare(id: number) {
    return this.http.get<FeatureCollection>(`/api/project/${id}/square-map`);
  }

  getDemographicAreas(id: number) {
    return this.http.get<FeatureCollection>(`/api/project/${id}/demographic-areas`)
      .pipe(tap(demographicAreas => this.updateUI(id, { demographicAreas })));
  }
}
